// GoalHistory.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
} from '@mui/material';
import { Add } from "@mui/icons-material";

// Import the original AddHrGoal component
import AddHrGoal from './AddHrGoal'; 
// Import the new EditGoalDetails component (or create it)
import EditGoalDetails from './EditGoalDetails'; 

const GoalHistory = () => {
  const theme = useTheme();
  
  // State for the Add Goal Modal
  const [openAddGoal, setOpenAddGoal] = useState(false);
  // State for the Edit Goal Modal
  const [openEditGoal, setOpenEditGoal] = useState(false);
  // State to hold the goal data when editing
  const [editingGoal, setEditingGoal] = useState(null); 

  // Dummy data for goals history (kept the same)
  const goalsHistoryData = [
    {
      id: 1,
      title: 'Q1 Sales Target', 
      employees: ['John Doe', 'Sarah Wilson', 'Mike Johnson', 'Jane Smith'], 
      status: 'Completed',
      createdDate: '2024-01-15',
    },
    {
      id: 2,
      title: 'Website Redesign',
      employees: ['Jane Smith', 'David Lee'],
      status: 'Draft', 
      createdDate: '2024-02-01',
    },
    {
      id: 3,
      title: 'HR Policy Documentation',
      employees: ['Mike Johnson'],
      status: 'Completed',
      createdDate: '2024-02-10',
    },
    {
      id: 4,
      title: 'Customer Onboarding Revamp',
      employees: ['Sarah Wilson', 'Chris Evans', 'Lisa Roy'],
      status: 'Draft',
      createdDate: '2024-01-20',
    }
  ];
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return theme.palette.success.main;
      case 'Draft': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const EmployeeListDisplay = ({ employees }) => {
    const displayCount = 3;
    const displayedEmployees = employees.slice(0, displayCount);
    const moreCount = employees.length - displayCount;

    return (
      <Box>
        {displayedEmployees.map((name, index) => (
          <Typography key={index} variant="body2" sx={{ lineHeight: 1.2 }}>
            {name}
          </Typography>
        ))}
        {moreCount > 0 && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
            {`+${moreCount} more`}
          </Typography>
        )}
      </Box>
    );
  };

  // Handler for adding a goal (uses setOpenAddGoal)
  const handleAddHrGoal = () => {
    setOpenAddGoal(true);
  }

  // Handler for editing a goal (sets data, uses setOpenEditGoal)
  const handleEditGoal = (goal) => {
    setEditingGoal(goal); // Set the goal data for the edit component
    setOpenEditGoal(true);
  }

  // Close handler for the Add Goal modal
  const handleCloseAddGoal = () => {
    setOpenAddGoal(false);
  }
  
  // Close handler for the Edit Goal modal (resets data too)
  const handleCloseEditGoal = () => {
    setOpenEditGoal(false);
    setEditingGoal(null);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Goals History
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{ borderRadius: 1 }}
         
        >
          Add Goal using Excel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{ borderRadius: 1 }}
          onClick={handleAddHrGoal} // Opens the AddHrGoal modal
        >
          Add Goal
        </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Goal Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Employees</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Created Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {goalsHistoryData.map((goal) => (
              <TableRow key={goal.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{goal.title}</TableCell>
                <TableCell>
                  <EmployeeListDisplay employees={goal.employees} />
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: getStatusColor(goal.status) + '20',
                      color: getStatusColor(goal.status),
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}
                  >
                    {goal.status}
                  </Box>
                </TableCell>
                <TableCell>{goal.createdDate}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }}>
                    View
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                    onClick={() => handleEditGoal(goal)} // Opens the EditGoalDetails modal
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 1. Add Goal Component */}
      <AddHrGoal 
        open={openAddGoal} 
        onClose={handleCloseAddGoal} 
     
      />

      {/* 2. Edit Goal Details Component */}      
     
        <EditGoalDetails 
          open={openEditGoal} 
          onClose={handleCloseEditGoal} 
          goalData={editingGoal} 
        />
      
    </Box>
  );
};

export default GoalHistory;